// noc.js — NOC Outages console: list/create outages and hand one off to
// Infra-Ops (raises a corrective-maintenance work order). Server calls go through
// api.fetch (cookie auth; throws on !ok) against /api/ticketing/noc/* and the
// infra lookups. DOM is resolved lazily on first show.

import { escapeHtml } from '../../src/lib/format.js';
import { openDialog, closeDialog } from '../../src/lib/dialog.js';
import { badge, titleCase } from '../../src/lib/hr-ui.js';
import * as api from '../api-client.js';
import * as notify from '../notifications.js';

const API = api.routes;
const flash = (m, t) => notify.showFlash(m, t);

const STATUS_BADGE = { active: 'warn', resolved: 'ok' };

let facilities = [];
let workSets = [];
let outages = [];
let domReady = false;
let el = {};

function fmt(v) {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}
function facilityName(id) { const f = facilities.find(x => x.id === id); return f ? f.name : null; }

function fillSelect(sel, items, { placeholder, labelFn }) {
  if (!sel) return;
  sel.innerHTML = [placeholder != null ? `<option value="">${escapeHtml(placeholder)}</option>` : '']
    .concat(items.map(i => `<option value="${escapeHtml(i.id)}">${escapeHtml(labelFn(i))}</option>`)).join('');
}

function resolveDom() {
  if (domReady) return;
  domReady = true;
  const g = (id) => document.getElementById(id);
  el = {
    statusFilter: g('nocStatusFilter'), refreshBtn: g('nocRefreshBtn'), newBtn: g('nocNewBtn'), tbody: g('nocTbody'),
    dlg: g('nocOutageDlg'), form: g('nocOutageForm'), scope: g('nocScope'), summary: g('nocSummary'),
    startedAt: g('nocStartedAt'), facility: g('nocFacility'), coverage: g('nocCoverage'),
    handoffDlg: g('nocHandoffDlg'), handoffForm: g('nocHandoffForm'), handoffWorkSet: g('nocHandoffWorkSet'),
    handoffPriority: g('nocHandoffPriority'), handoffSummary: g('nocHandoffSummary'),
  };
  wireEvents();
}

async function init() {
  resolveDom();
  try {
    await ensureLookups();
    await loadList();
  } catch (e) { flash(e.message || 'Failed to load outages', 'error'); }
}

let lookupsLoaded = false;
async function ensureLookups() {
  if (lookupsLoaded) return;
  // Infra lookups may 403 for a pure noc_admin without infra read — tolerate it.
  const [facs, sets] = await Promise.all([
    api.fetch(API.infraFacilities(), { method: 'GET' }).catch(() => ({ items: [] })),
    api.fetch(API.infraWorkSets(), { method: 'GET' }).catch(() => []),
  ]);
  facilities = (facs && facs.items) || [];
  workSets = sets || [];
  lookupsLoaded = true;
  fillSelect(el.facility, facilities, { placeholder: '— none —', labelFn: f => f.name });
  fillSelect(el.handoffWorkSet, workSets, { placeholder: 'Select…', labelFn: w => w.name });
}

async function loadList() {
  const p = new URLSearchParams();
  if (el.statusFilter.value) p.set('status', el.statusFilter.value);
  const data = await api.fetch(API.nocOutages(p.toString()), { method: 'GET' });
  outages = data.items || [];
  render();
}

function render() {
  if (!outages.length) { el.tbody.innerHTML = '<tr><td colspan="8" style="color:var(--sub);">No outages.</td></tr>'; return; }
  el.tbody.innerHTML = outages.map(o => {
    const canHandoff = o.status === 'active' && !o.work_order_id;
    const wo = o.work_order_id ? badge('handed off', 'info') : '—';
    return `<tr>
      <td>${escapeHtml(o.summary)}</td>
      <td>${badge(titleCase(o.scope), 'off')}</td>
      <td>${escapeHtml(facilityName(o.facility_id) || '—')}</td>
      <td>${escapeHtml(fmt(o.started_at))}</td>
      <td>${escapeHtml(fmt(o.etr))}</td>
      <td>${wo}</td>
      <td>${badge(titleCase(o.status), STATUS_BADGE[o.status] || '')}</td>
      <td>${canHandoff ? `<button class="btn ghost" data-handoff="${escapeHtml(o.id)}">Hand off →</button>` : ''}</td>
    </tr>`;
  }).join('');
}

// --- create ---
function openNew() {
  el.form.reset();
  fillSelect(el.facility, facilities, { placeholder: '— none —', labelFn: f => f.name });
  openDialog(el.dlg);
}

async function submitNew(ev) {
  ev.preventDefault();
  const summary = el.summary.value.trim();
  if (!summary || !el.startedAt.value) { flash('Summary and start time are required', 'error'); return; }
  const coverage = el.coverage.value.split(',').map(s => s.trim()).filter(Boolean);
  const body = {
    scope: el.scope.value, summary, started_at: new Date(el.startedAt.value).toISOString(),
    facility_id: el.facility.value || null,
    coverage_areas: coverage.length ? coverage : null,
  };
  try {
    await api.fetch(API.nocOutages(), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.dlg);
    flash('Outage created', 'success');
    await loadList();
  } catch (e) { flash(e.message || 'Create failed', 'error'); }
}

// --- handoff ---
function openHandoff(outage) {
  el.handoffForm.dataset.outageId = outage.id;
  el.handoffSummary.textContent = outage.summary;
  fillSelect(el.handoffWorkSet, workSets, { placeholder: 'Select…', labelFn: w => w.name });
  openDialog(el.handoffDlg);
}

async function submitHandoff(ev) {
  ev.preventDefault();
  const id = el.handoffForm.dataset.outageId;
  if (!el.handoffWorkSet.value) { flash('Pick a work set', 'error'); return; }
  const body = { work_set_id: el.handoffWorkSet.value, priority: el.handoffPriority.value };
  try {
    await api.fetch(API.nocOutageHandoff(id), { method: 'POST', body: JSON.stringify(body) });
    closeDialog(el.handoffDlg);
    flash('Handed off to Operations', 'success');
    await loadList();
  } catch (e) { flash(e.message || 'Handoff failed', 'error'); }
}

function wireEvents() {
  el.refreshBtn?.addEventListener('click', () => init());
  el.newBtn?.addEventListener('click', openNew);
  el.statusFilter?.addEventListener('change', () => loadList());
  el.tbody?.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-handoff]');
    if (btn) openHandoff(outages.find(o => o.id === btn.dataset.handoff));
  });
  el.form?.addEventListener('submit', submitNew);
  el.handoffForm?.addEventListener('submit', submitHandoff);
  document.querySelectorAll('[data-dialog-dismiss]').forEach(b => {
    if (b.dataset.nocWired || !['nocOutageDlg', 'nocHandoffDlg'].includes(b.dataset.dialogDismiss)) return;
    b.dataset.nocWired = '1';
    b.addEventListener('click', () => closeDialog(document.getElementById(b.dataset.dialogDismiss)));
  });
}

export default { init };
